import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminVideoImport, adminPollVideoImport, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Clapperboard, Loader2, Plane, Camera, Pencil, CheckCircle2, MinusCircle, XCircle } from "lucide-react";

export default function AdminVideoBulkImport() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [publish, setPublish] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // {processed,total,created,skipped}
  const [result, setResult] = useState(null); // final job snapshot

  const runImport = async () => {
    const urls = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast.error("Paste at least one YouTube/Vimeo URL or a playlist link.");
      return;
    }
    setRunning(true);
    setResult(null);
    setProgress(null);
    try {
      const job = await adminVideoImport(urls, publish);
      toast.message(`Import started · ${job.total} video${job.total !== 1 ? "s" : ""} queued`, { id: "vimport" });
      const final = await adminPollVideoImport(job.job_id, {
        onProgress: (st) => {
          setProgress({ processed: st.processed || 0, total: st.total || job.total, created: st.created || 0, skipped: st.skipped || 0 });
          toast.message(`Importing · ${st.processed || 0}/${st.total || job.total}`, {
            id: "vimport",
            description: `created ${st.created || 0} · skipped ${st.skipped || 0}`,
          });
        },
      });
      setResult(final);
      if (final.status === "completed") {
        toast.success(`Import complete · ${final.created} created · ${final.skipped} skipped${(final.errors || []).length ? ` · ${final.errors.length} errors` : ""}`, { id: "vimport" });
      } else if (final.status === "failed") {
        toast.error(final.error || "Import failed.", { id: "vimport" });
      } else {
        toast.warning("Import still running in the background, check the gallery in a minute.", { id: "vimport" });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed to start.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-4xl">
      <Link
        to="/admin/gallery"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white mb-6"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">Bulk import</div>
      <h1 className="font-display text-4xl tracking-tight">Import videos</h1>
      <p className="text-[13px] text-[#A3A3A3] mt-3 max-w-2xl">
        Paste YouTube or Vimeo links - one per line - or a whole YouTube playlist URL.
        Each video gets its official thumbnail, title and description pulled in automatically,
        and AI works out whether it's an aerial or ground shot (editable afterwards).
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder={"https://youtu.be/xxxxxxxxxxx\nhttps://www.youtube.com/playlist?list=PLxxxxxxxx\nhttps://vimeo.com/123456789"}
        className="hl-input mt-8 font-mono text-[13px]"
        data-testid="video-import-textarea"
        disabled={running}
      />

      <label className="mt-4 inline-flex items-center gap-3 text-[12px] text-[#c9d1d9]">
        <input
          type="checkbox"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
          data-testid="video-import-publish"
          className="w-4 h-4 accent-[#4A6D8C]"
          disabled={running}
        />
        Publish immediately (otherwise saved as drafts for review)
      </label>

      <div className="mt-6">
        <button
          type="button"
          onClick={runImport}
          disabled={running || !text.trim()}
          data-testid="video-import-start"
          className="hl-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Clapperboard size={14} />}
          {running
            ? progress
              ? `Importing · ${progress.processed}/${progress.total}`
              : "Starting import…"
            : "Import videos"}
        </button>
      </div>

      {result && (
        <div className="mt-10" data-testid="video-import-results">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-4">
            Results · {result.created} created · {result.skipped} skipped
            {(result.errors || []).length > 0 ? ` · ${result.errors.length} errors` : ""}
          </div>
          <div className="flex flex-col gap-2">
            {(result.items || []).map((it, idx) => (
              <div
                key={`${it.url}-${idx}`}
                className="flex items-center gap-4 bg-[#0f0f0f] border border-[#1a1a1a] p-3"
                data-testid={`video-import-row-${idx}`}
              >
                {it.thumbnail ? (
                  <img src={resolveMedia(it.thumbnail)} alt="" className="w-20 h-12 object-cover bg-[#0a0a0a]" />
                ) : (
                  <div className="w-20 h-12 bg-[#0a0a0a]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white truncate">{it.title || it.url}</div>
                  <div className="text-[11px] text-[#737373] flex items-center gap-2 mt-0.5">
                    {it.status === "created" ? (
                      <><CheckCircle2 size={12} className="text-[#658CAE]" /> Created{publish ? " · published" : " · draft"}</>
                    ) : (
                      <><MinusCircle size={12} /> Skipped - {it.reason}</>
                    )}
                    {it.shot_type && (
                      <span className="inline-flex items-center gap-1 border border-[#262626] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em]">
                        {it.shot_type === "aerial" ? <Plane size={10} /> : <Camera size={10} />}
                        {it.shot_type}
                      </span>
                    )}
                  </div>
                </div>
                {(it.id || it.existing_id) && (
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/gallery/${it.id || it.existing_id}`)}
                    data-testid={`video-import-edit-${it.id || it.existing_id}`}
                    className="text-[#A3A3A3] hover:text-white p-2"
                    title={it.id ? "Edit" : "Edit the existing item"}
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            ))}
            {(result.errors || []).map((er, idx) => (
              <div key={`err-${idx}`} className="flex items-center gap-3 bg-[#0f0f0f] border border-red-900/40 p-3 text-[12px] text-red-300">
                <XCircle size={13} /> <span className="truncate">{er.url}</span> - {er.error}
              </div>
            ))}
          </div>
          {result.created > 0 && !publish && (
            <div className="text-[12px] text-[#A3A3A3] mt-4">
              Imported videos are saved as <span className="text-white">drafts</span>, review them in{" "}
              <Link to="/admin/gallery" className="text-[#658CAE] hover:text-white underline">Manage gallery</Link>{" "}
              and publish when ready.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
